import React, { type FC, memo, type ReactNode } from 'react';
import { mergeProps } from '../../../utils/merge-props/merge-props';
import { propsEqualityFn } from '../../../utils/object-utils';
import { cn } from '../../../utils/ui';
import CharacterCounter, { type CharacterCounterProps } from '../../CharacterCounter/CharacterCounter';
import Text, { type TextProps } from '../../Text/Text';
import FileInput, { type FileInputProps } from '../FileInput/FileInput';
import Input, { type InputProps } from '../Input/Input';
import TextareaInput, { type TextareaInputProps } from '../TextareaInput/TextareaInput';
import FieldLabel, { type FieldLabelProps } from './FieldLabel';

export interface FieldProps {
    className?: string;
    fileInputProps?: FileInputProps;
    inputProps?: InputProps;
    textareaProps?: TextareaInputProps;
    fieldLaelProps?: FieldLabelProps;
    error?: string;
    onErrorClick?: () => void;
    renderLabelInfo?: () => ReactNode;
    renderCustomInput?: () => ReactNode;
    preview?: string;
    folatingInputLabelProps?: TextProps;
    characterCounterProps?: CharacterCounterProps;
    children?: ReactNode;
}

const Field: FC<FieldProps> = props => {
    const { className } = props;

    function getBaseClassName() {
        return cn('transition relative text-gray-400 dark:text-gray-600 focus-within:text-gray-900 dark:focus-within:text-gray-50', className);
    }
    function renderError(): ReactNode {
        const { error, onErrorClick } = props;
        if (!error) return null;

        return (
            <Text className="text-sm text-red-500" enableMultiline={true} onClick={onErrorClick}>
                {error}
            </Text>
        );
    }

    function renderInput(): ReactNode {
        const { inputProps, error } = props;
        if (!inputProps) return null;

        const _props = mergeProps<[Partial<InputProps>, Partial<InputProps>]>(
            {
                className: 'h-10 text-base',
                hasError: Boolean(error),
            },
            inputProps
        );

        return <Input {..._props} />;
    }

    function renderFileInput(): ReactNode {
        const { fileInputProps, error } = props;
        if (!fileInputProps) return null;

        return <FileInput hasError={Boolean(error)} {...fileInputProps} />;
    }

    function renderTextareaInput(): ReactNode {
        const { textareaProps, error } = props;
        if (!textareaProps) return null;

        return <TextareaInput hasError={Boolean(error)} {...textareaProps} />;
    }

    // function renderSelectInput(): ReactNode {
    //     const { selectInputProps, error } = props;
    //     if (!selectInputProps) return null;

    //     return (
    //         <SelectInput hasError={Boolean(error)} {...selectInputProps} />
    //     );
    // }

    function renderCustomInput(): ReactNode {
        const { renderCustomInput } = props;
        if (!renderCustomInput) return null;

        return renderCustomInput();
    }

    function renderLabel(): ReactNode {
        const { fieldLaelProps } = props;
        if (!fieldLaelProps) return null;

        const _props = mergeProps<[Partial<FieldLabelProps>, FieldLabelProps]>(
            {
                className: 'mb-2',
            },
            fieldLaelProps
        );

        return <FieldLabel {..._props} />;
    }

    function renderLabelInfo(): ReactNode {
        const { renderLabelInfo } = props;
        if (!renderLabelInfo) return null;

        return renderLabelInfo();
    }

    function renderFloatingInputLabel(): ReactNode {
        const { folatingInputLabelProps } = props;
        if (!folatingInputLabelProps) return null;

        const _props = mergeProps<[Partial<TextProps>, TextProps]>(
            {
                className: 'absolute top-[40px] right-2 text-sm',
            },
            folatingInputLabelProps
        );

        return <Text className="" {..._props} />;
    }

    function renderInputCounter(): ReactNode {
        const { characterCounterProps } = props;
        if (!characterCounterProps) return null;

        const _props = mergeProps<[Partial<CharacterCounterProps>, CharacterCounterProps]>(
            {
                className: 'absolute text-xxs top-full right-1',
            },
            characterCounterProps
        );

        return <CharacterCounter {..._props} />;
    }

    return (
        <div className={getBaseClassName()}>
            {renderLabel()}
            {renderInput()}
            {renderFloatingInputLabel()}
            {renderInputCounter()}
            {renderTextareaInput()}
            {renderLabelInfo()}
            {renderFileInput()}
            {/* {renderSelectInput()} */}
            {renderCustomInput()}
            {renderError()}
        </div>
    );
};

export default memo(Field, propsEqualityFn);
